import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { Toast, ToastProps } from '../uikit';

// COM-647 Task 3: the shared toast host. Before this, THREE places each kept
// their own copy of "useState + setTimeout + an inline <Toast>":
//   - SettingsPage.tsx (auto-hides after 3s, and ALSO clears its error toast
//     early once the underlying validation issue is fixed - see that page's
//     own effect, kept there rather than moved here since it's a
//     SettingsPage-specific rule about ITS OWN toast content, not something
//     a generic host should know about)
//   - AllArticlesPage.tsx (never auto-hides - a fetch-error toast stays until
//     the user dismisses it)
//   - SourcesContext.tsx's `SourcesProvider`, which rendered its OWN raw MUI
//     `Snackbar`/`Alert` for the socket-level 'error' event, always with
//     `severity: 'error'` and MUI's own 3s `autoHideDuration` - a fourth
//     copy of the same idea, just built on a different (non-kit) component.
// Folded ALL FOUR into this one provider + `useToast()` hook rather than
// "move the three pages, leave SourcesProvider's Snackbar alone": the kit's
// `Toast` (uikit/Toast.tsx) is the only visual language this app uses
// everywhere else, `SourcesProvider`'s Snackbar/Alert was the one remaining
// place still rendering the pre-kit MUI look, and both were already
// anchored to the same corner (`AllArticlesPage`'s fixed top:16/right:16 vs
// the Snackbar's `anchorOrigin={{top,right}}`) - leaving both wired up
// side by side risked two toasts stacking in the same corner if a socket
// error and a page-level failure ever landed together. `SourcesProvider`
// only ever raises 'error' severity, which the kit's `Toast` already
// supports, so nothing about that call site needed a shape `Toast` can't
// express.
//
// `autoHideMs` is opt-in per call, not a fixed policy the host imposes:
// AllArticlesPage's persistent-until-dismissed behavior and SettingsPage's
// 3s auto-hide are genuinely different UX choices made by their own
// callers, and a single host-wide duration would have silently changed one
// of them.
export type ToastSeverity = ToastProps['severity'];

export interface ShowToastOptions {
  // Milliseconds until the toast clears itself. Omit for a toast that stays
  // up until `hideToast()` is called (or another `showToast()` replaces it) -
  // this is AllArticlesPage's existing "persists until dismissed" behavior.
  autoHideMs?: number;
}

interface ToastState {
  message: string;
  severity: ToastSeverity;
}

interface ToastContextType {
  // The toast currently on screen, or null. Exposed (not just an internal
  // implementation detail) because SettingsPage's own effect needs to read
  // `toast?.severity` to decide whether ITS error toast should clear early -
  // see that page's own comment.
  toast: ToastState | null;
  showToast: (message: string, severity?: ToastSeverity, options?: ShowToastOptions) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hideToast = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = 'info', options?: ShowToastOptions) => {
      // A new `showToast()` call replaces whatever is currently shown -
      // including its own pending auto-hide timer, so a fast-following
      // second toast (e.g. save-error then save-success) doesn't get cut
      // short by the FIRST toast's timer, or leak that timer running after
      // its own message no longer matches what's on screen.
      clearTimer();
      setToast({ message, severity });

      if (options?.autoHideMs) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setToast(null);
        }, options.autoHideMs);
      }
    },
    [clearTimer]
  );

  // Unmount safety: nothing catastrophic leaks if this fires after unmount
  // (it only calls `clearTimeout`), but there's no reason to leave a timer
  // running past the provider's own lifetime.
  useEffect(() => clearTimer, [clearTimer]);

  const value = useMemo<ToastContextType>(() => ({ toast, showToast, hideToast }), [toast, showToast, hideToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: (t) => t.zIndex.snackbar }}>
          <Toast message={toast.message} severity={toast.severity} onClose={hideToast} />
        </Box>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
